import React, { useState } from 'react';
import { FaMapMarkerAlt, FaDollarSign, FaUsers, FaUtensils, FaBriefcase, FaTimes, FaPaperPlane } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './JobCard.css';

const COUNTRY_FLAGS = {
  'UAE': '🇦🇪', 'Saudi Arabia': '🇸🇦', 'Kuwait': '🇰🇼', 'Qatar': '🇶🇦',
  'Bahrain': '🇧🇭', 'Oman': '🇴🇲', 'Singapore': '🇸🇬', 'Malaysia': '🇲🇾', 'Libya': '🇱🇾'
};

const JobCard = ({ job }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [applyModal, setApplyModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleApplyClick = () => {
    if (!user) {
      toast.error('Please login first to apply for a job');
      navigate('/login');
      return;
    }
    setApplyModal(true);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      toast.error('Please upload your resume to apply');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('job_id', job.id);
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('message', form.message);
      fd.append('resume', resumeFile);

      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('pavishna_token')}`
        },
        body: fd
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 409) {
          toast.error('You already applied for this job.');
        } else {
          toast.error(data.message || 'Failed to submit application.');
        }
        return;
      }
      toast.success('Application submitted! We will contact you soon.');
      setApplyModal(false);
      setForm({ name: '', email: '', phone: '', message: '' });
      setResumeFile(null);
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const imgSrc = job.image_url
    ? `http://localhost:5000${job.image_url}`
    : `https://source.unsplash.com/400x220/?${encodeURIComponent(job.job_role + ' work')}`;

  return (
    <>
      <div className="job-card">
        <div className="job-card-img-wrap">
          <img src={imgSrc} alt={job.job_role} className="job-card-img" onError={e => { e.target.src = 'https://source.unsplash.com/400x220/?professional,work'; }} />
          <span className="job-country-badge">{COUNTRY_FLAGS[job.country] || '🌍'} {job.country}</span>
        </div>
        <div className="job-card-body">
          <div className="job-category-tag">{job.category || 'General'}</div>
          <h3 className="job-role">{job.job_role}</h3>
          <div className="job-meta">
            <div className="meta-item">
              <FaDollarSign />
              <span>{job.salary || 'Negotiable'}</span>
            </div>
            <div className="meta-item">
              <FaUsers />
              <span>{job.num_vacancies} {job.num_vacancies === 1 ? 'Vacancy' : 'Vacancies'}</span>
            </div>
            <div className="meta-item">
              <FaUtensils />
              <span className={`food-badge ${job.food_accommodation === 'Provided' ? 'yes' : job.food_accommodation === 'Partially Provided' ? 'partial' : 'no'}`}>
                Food & Stay: {job.food_accommodation}
              </span>
            </div>
          </div>
          {job.additional_details && (
            <p className="job-details-preview">{job.additional_details.substring(0, 80)}{job.additional_details.length > 80 ? '...' : ''}</p>
          )}
          <div className="job-card-actions">
            <button className="btn-view-job" onClick={() => setShowModal(true)}>View Details</button>
            <button className="btn-apply-job" onClick={handleApplyClick}>Apply Now</button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}><FaTimes /></button>
            <img src={imgSrc} alt={job.job_role} className="modal-img" onError={e => { e.target.src = 'https://source.unsplash.com/600x300/?professional,work'; }} />
            <div className="modal-content">
              <div className="modal-country">{COUNTRY_FLAGS[job.country] || '🌍'} {job.country}</div>
              <h2 className="modal-title">{job.job_role}</h2>
              <div className="modal-meta-grid">
                <div className="modal-meta-item"><FaDollarSign /><div><span>Salary</span><strong>{job.salary || 'Negotiable'}</strong></div></div>
                <div className="modal-meta-item"><FaUsers /><div><span>Vacancies</span><strong>{job.num_vacancies}</strong></div></div>
                <div className="modal-meta-item"><FaBriefcase /><div><span>Category</span><strong>{job.category || 'General'}</strong></div></div>
                <div className="modal-meta-item"><FaMapMarkerAlt /><div><span>Location</span><strong>{job.country}</strong></div></div>
              </div>
              <div className="modal-section">
                <h4>Food & Accommodation</h4>
                <span className={`food-badge large ${job.food_accommodation === 'Provided' ? 'yes' : job.food_accommodation === 'Partially Provided' ? 'partial' : 'no'}`}>
                  {job.food_accommodation}
                </span>
              </div>
              {job.additional_details && (
                <div className="modal-section">
                  <h4>Additional Details</h4>
                  <p>{job.additional_details}</p>
                </div>
              )}
              <button className="btn-primary w-full" onClick={() => { setShowModal(false); handleApplyClick(); }}>
                <FaPaperPlane /> Apply for this Position
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {applyModal && (
        <div className="modal-overlay" onClick={() => setApplyModal(false)}>
          <div className="modal-box apply-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setApplyModal(false)}><FaTimes /></button>
            <div className="apply-header">
              <h2>Apply for <span>{job.job_role}</span></h2>
              <p>{COUNTRY_FLAGS[job.country] || '🌍'} {job.country} · {job.salary || 'Salary Negotiable'}</p>
            </div>
            <form onSubmit={handleApply} className="apply-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    className="form-control"
                    value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Message / Cover Note</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  placeholder="Tell us about your experience and why you're interested..."
                />
              </div>

              {/* Resume Upload — compulsory */}
              <div className="form-group">
                <label>Resume / CV * <span className="required-note">(PDF, DOC, DOCX — Max 5MB)</span></label>
                <div
                  className={`resume-upload-area ${resumeFile ? 'has-file' : ''}`}
                  onClick={() => document.getElementById(`resume-inp-${job.id}`).click()}
                >
                  {resumeFile ? (
                    <div className="resume-file-info">
                      <span className="resume-icon">📄</span>
                      <div>
                        <strong>{resumeFile.name}</strong>
                        <span>{(resumeFile.size / 1024).toFixed(0)} KB</span>
                      </div>
                      <button
                        type="button"
                        className="remove-resume"
                        onClick={(e) => { e.stopPropagation(); setResumeFile(null); }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="resume-placeholder">
                      <span>📎</span>
                      <span>Click to upload Resume / CV</span>
                      <small>PDF, DOC, DOCX up to 5MB</small>
                    </div>
                  )}
                  <input
                    id={`resume-inp-${job.id}`}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setResumeFile(file);
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Submitting...' : <><FaPaperPlane /> Submit Application</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default JobCard;